import { Component, createSignal, onMount, Show, createMemo } from 'solid-js';
import DataTable, { Column } from '../components/tables/DataTable';
import TableSkeleton from '../components/ui/TableSkeleton';
import SearchInput from '../components/ui/SearchInput';
import CountryFlag from '../components/ui/CountryFlag';
import NetworkLogo from '../components/ui/NetworkLogo';
import { TbCoins, TbFilter, TbActivity } from 'solid-icons/tb';
import { OcFeedtrophy2 } from 'solid-icons/oc';
import PlatformIcon, { getPlatformColor } from '../components/ui/PlatformIcon';
import { conversionsApi } from '../services/api';
import { useSocketEvent } from '../services/socket';
import { setDocumentTitle } from '../utils/document';
import { getTodayGMT, getYesterdayGMT, formatGMTTime, formatWIBTime, isTodayGMT, formatDateWithDay } from '../utils/timezone';
import { getConversionsColumns } from '../utils/conversionsColumns';
import type { Conversion } from '../types';
import moment from 'moment';
import './NewRowAnimation.css';

interface ConversionWithAnimation extends Conversion {
  _isNew?: boolean;
}

const Conversions: Component = () => {
  const [conversions, setConversions] = createSignal<ConversionWithAnimation[]>([]);
  const [loading, setLoading] = createSignal(true);
  const [selectedDate, setSelectedDate] = createSignal<string>(getYesterdayGMT());
  const [realtimeCount, setRealtimeCount] = createSignal(0);
  const [lastConversionTime, setLastConversionTime] = createSignal<string | null>(null);
  const [totalRealtimePayout, setTotalRealtimePayout] = createSignal(0);
  const [searchInput, setSearchInput] = createSignal('');

  // Filtered conversions based on search input (supports comma-separated OR filter)
  const filteredConversions = createMemo(() => {
    const search = searchInput().toLowerCase().trim();
    if (!search) return conversions();

    // Split by comma and filter empty terms - supports "term1,term2,term3" format
    const searchTerms = search.split(',').map(term => term.trim()).filter(Boolean);
    if (searchTerms.length === 0) return conversions();

    // Filter: each row must match at least ONE search term (OR logic)
    return conversions().filter(conv =>
      searchTerms.some(term =>
        conv.user?.toLowerCase().includes(term) ||
        conv.country?.toLowerCase().includes(term) ||
        conv.network?.toLowerCase().includes(term) ||
        conv.ip_address?.toLowerCase().includes(term)
      )
    );
  });

  // Calculate filtered earning in a single pass (optimized)
  const filteredEarning = createMemo(() => {
    return filteredConversions().reduce((sum, conv) => sum + Number(conv.payout || 0), 0);
  });

  // Get icon based on traffic source (where traffic came from)
  const getTrafficSourceIcon = (conversion: Conversion) => {
    return (
      <PlatformIcon
        platform={conversion.traffic_source}
        size={16}
        class={`${getPlatformColor(conversion.traffic_source)}`}
      />
    );
  };

  const loadConversions = async () => {
    setLoading(true);
    setConversions([]);
    try {
      const data = await conversionsApi.getAll({
        startDate: selectedDate(),
        endDate: selectedDate(),
      });
      setConversions(data.data || []);
    } catch (error) {
      setConversions([]);
    } finally {
      setLoading(false);
    }
  };

  const handleDateChange = (newDate?: string) => {
    if (newDate !== undefined) {
      setSelectedDate(newDate);
    }
    loadConversions();
  };

  // Calculate earning breakdown in a single pass (optimized performance)
  const earningBreakdown = createMemo(() => {
    const conversionsData = conversions();
    const totals = {
      total: 0,
      byNetwork: {} as Record<string, number>,
      byCountry: {} as Record<string, number>,
      byTraffic: {} as Record<string, number>,
      byUser: {} as Record<string, number>,
    };

    conversionsData.forEach(conv => {
      const payout = Number(conv.payout || 0);
      totals.total += payout;

      // By network
      if (!totals.byNetwork[conv.network]) totals.byNetwork[conv.network] = 0;
      totals.byNetwork[conv.network] += payout;

      // By country
      if (!totals.byCountry[conv.country]) totals.byCountry[conv.country] = 0;
      totals.byCountry[conv.country] += payout;

      // By traffic source
      const traffic = conv.traffic_source || 'Unknown';
      if (!totals.byTraffic[traffic]) totals.byTraffic[traffic] = 0;
      totals.byTraffic[traffic] += payout;

      // By user
      if (!totals.byUser[conv.user]) totals.byUser[conv.user] = 0;
      totals.byUser[conv.user] += payout;
    });

    return totals;
  });

  // Get total payout
  const totalPayout = () => earningBreakdown().total;

  // Get earning by network
  const getNetworkEarning = (network: string) => earningBreakdown().byNetwork[network] || 0;

  // Get earning by country
  const getCountryEarning = (country: string) => earningBreakdown().byCountry[country] || 0;

  // Get earning by traffic source
  const getTrafficEarning = (traffic: string) => earningBreakdown().byTraffic[traffic] || 0;

  // Get earning by user
  const getUserEarning = (user: string) => earningBreakdown().byUser[user] || 0;

  // Calculate top 1 earner
  const topEarner = createMemo(() => {
    const userEarnings = earningBreakdown().byUser;
    if (Object.keys(userEarnings).length === 0) return null;

    const topEntry = Object.entries(userEarnings).sort(([, a], [, b]) => b - a)[0];
    return topEntry?.[0] || null;
  });

  // Check if user is top 1 earner
  const isTopEarner = (user: string): boolean => {
    return topEarner() === user;
  };

  // Use shared conversions columns utility with render functions
  const columns: Column<Conversion>[] = getConversionsColumns({
    isTopEarner,
    renderGMTCell: (conversion) => (
      <span class="text-sm">{formatGMTTime(conversion.datetime)}</span>
    ),
    renderWIBCell: (conversion) => (
      <span class="text-sm">{formatWIBTime(conversion.datetime)}</span>
    ),
    renderUserCell: (conversion) => {
      if (isTopEarner(conversion.user)) {
        return (
          <div class="flex items-center gap-2">
            <span class="font-bold text-gray-900 dark:text-white">{conversion.user}</span>
            <OcFeedtrophy2 size={16} class="text-amber-400" />
          </div>
        );
      }
      return <span class="font-mono text-sm">{conversion.user}</span>;
    },
    renderAccessCell: (conversion) => (
      <div class="flex gap-1 items-center justify-center">
        <CountryFlag code={conversion.country || 'xx'} size={16} />
        {getTrafficSourceIcon(conversion)}
        <NetworkLogo network={conversion.network} size={16} />
      </div>
    ),
    renderNetworkCell: (conversion) => null, // Merged into Access cell
    renderEarningCell: (conversion) => (
      <div class="flex items-center justify-end gap-1">
        <span class="text-sm">${Number(conversion.payout).toFixed(2)}</span>
        <TbCoins size={16} class="text-amber-400" />
      </div>
    ),
    renderIPCell: (conversion) => (
      <span class="font-mono text-xs text-gray-600 dark:text-gray-400">
        {conversion.ip_address || '-'}
      </span>
    ),
  });

  // Get row class for duplicates and new row animation
  const getRowClass = (conversion: ConversionWithAnimation): string => {
    let classes = '';

    // Add animation for new conversions
    if (conversion._isNew) {
      classes += 'new-row-animate new-row-highlight ';
    }

    // Add styling for duplicates
    if (conversion.is_duplicate) {
      classes += 'bg-red-100 dark:bg-red-900/40 hover:bg-red-200 dark:hover:bg-red-800/50 border-l-4 border-red-600 dark:border-red-500 font-medium';
    }

    return classes;
  };

  onMount(() => {
    // Set page title
    setDocumentTitle('Conversions');

    // Load conversions
    loadConversions();

    // Handle new conversion (notifications handled globally in Layout)
    const handleNewConversion = (data: Conversion) => {
      // Add new conversion to the beginning of the list
      setConversions((prevConversions) => {
        const newConversion: ConversionWithAnimation = {
          ...data,
          datetime: data.datetime || new Date().toISOString(),
          _isNew: true,
        };

        // Only show if conversion is from today (GMT+0)
        if (!isTodayGMT(newConversion.datetime)) {
          return prevConversions;
        }

        // Increment realtime counter
        setRealtimeCount((c) => c + 1);
        setLastConversionTime(newConversion.datetime);
        setTotalRealtimePayout((p) => p + Number(data.payout || 0));

        // Keep only recent 1000 items in memory
        const updated = [newConversion, ...prevConversions].slice(0, 1000);

        // Remove _isNew flag after animation completes (0.4s for slide-in + 2s for highlight = 2.4s)
        setTimeout(() => {
          setConversions((current) =>
            current.map((conversion) =>
              conversion._isNew ? { ...conversion, _isNew: false } : conversion
            )
          );
        }, 2400);

        return updated;
      });
    };

    // Set up real-time socket listener for new conversions
    useSocketEvent<Conversion>('conversion-recorded', handleNewConversion);

    // Also listen for new-conversion events as backup
    useSocketEvent<Conversion>('new-conversion', handleNewConversion);
  });

  return (
    <div class="space-y-6">
        {/* Header */}
        <div class="flex items-center justify-between">
          <div>
            <h1 class="text-2xl font-bold text-gray-900 dark:text-white">
              Conversions
            </h1>
            <p class="text-sm text-gray-500 dark:text-gray-400 mt-1">
              {realtimeCount() > 0 ? (
                <>
                  Receiving real-time conversions {lastConversionTime() && `(last ${formatRelativeTime(lastConversionTime()!)})`}
                </>
              ) : (
                'All conversion data by date range'
              )}
            </p>
          </div>
          <div class="text-right">
            <p class="text-sm text-gray-500 dark:text-gray-400">
              Showing {conversions().length} conversions
            </p>
            <p class="text-lg font-bold text-gray-900 dark:text-white flex items-center gap-1 justify-end">
              <TbCoins size={20} class="text-amber-400" />
              ${(totalPayout() + totalRealtimePayout()).toFixed(2)}
              {totalRealtimePayout() > 0 && (
                <span class="text-xs font-normal text-green-600 dark:text-green-400">
                  (+${totalRealtimePayout().toFixed(2)})
                </span>
              )}
            </p>
          </div>
        </div>

        {/* Date Filter */}
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow p-4">
          <div class="flex flex-col md:flex-row md:items-center md:justify-between gap-4">

            <div class="flex items-center gap-4">
              <div class="flex flex-col gap-1">
                <label class="text-xs text-gray-500 dark:text-gray-400">Date</label>
                <div class="flex items-center gap-2">
                  <input
                    type="date"
                    value={selectedDate()}
                    onChange={(e) => handleDateChange(e.currentTarget.value)}
                    class="px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
                  />
                  <span class="text-xs text-gray-500 dark:text-gray-400 whitespace-nowrap">
                    {formatDateWithDay(selectedDate())}
                  </span>
                </div>
              </div>
            </div>

            {/* Total Filtered Earning - Stacks below on mobile, inline on md+ */}
            <Show when={searchInput().trim().length > 0 && (filteredConversions().length > 0 || totalRealtimePayout() > 0)}>
              <div class="text-right md:text-right w-full md:w-auto">
                <p class="text-xs text-gray-500 dark:text-gray-400">Total Filtered Earning</p>
                <p class="text-lg font-bold text-amber-600 dark:text-amber-400 flex items-center gap-1 justify-start md:justify-end">
                  <TbCoins size={16} />
                  ${(filteredEarning() + totalRealtimePayout()).toFixed(2)}
                  {totalRealtimePayout() > 0 && (
                    <span class="text-xs font-normal text-green-600 dark:text-green-400">
                      (+${totalRealtimePayout().toFixed(2)})
                    </span>
                  )}
                </p>
              </div>
            </Show>
          </div>
        </div>

        {/* Data Table Wrapper */}
        <div class="bg-white dark:bg-gray-800 rounded-lg shadow w-full">
          <SearchInput
            value={searchInput()}
            onInput={(e) => setSearchInput(e)}
            placeholder="Search by User, Country, Network, or IP..."
          />

          <div class="overflow-x-auto">
            {loading() ? (
              <TableSkeleton rows={5} columns={columns.length} />
            ) : (
              <DataTable
                data={filteredConversions()}
                columns={columns}
                loading={loading()}
                searchable={false}
                defaultSortKey="datetime"
                defaultSortOrder="desc"
                getRowClass={getRowClass}
              />
            )}
          </div>
        </div>
    </div>
  );
};

export default Conversions;
